import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  XMarkIcon,
  CalendarIcon,
  ClockIcon,
  PlusIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { advisorService } from '@/services/api/advisorService';
import type { Advisor, AvailabilitySchedule } from '@/types/advisor';
import { useHolidayMap, useHolidayMapMultiYear } from '@/hooks/useHolidays';

type DayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

interface DaySchedule {
  active: boolean;
  start: string;
  end: string;
}

interface TimeOffEntry {
  start_date: string;
  end_date: string;
  reason: string;
  saved?: boolean;
}

const DAYS: { key: DayKey; label: string; short: string }[] = [
  { key: 'monday', label: 'Lunes', short: 'Lu' },
  { key: 'tuesday', label: 'Martes', short: 'Ma' },
  { key: 'wednesday', label: 'Miércoles', short: 'Mi' },
  { key: 'thursday', label: 'Jueves', short: 'Ju' },
  { key: 'friday', label: 'Viernes', short: 'Vi' },
  { key: 'saturday', label: 'Sábado', short: 'Sá' },
  { key: 'sunday', label: 'Domingo', short: 'Do' },
];

const DEFAULT_SCHEDULE: Record<DayKey, DaySchedule> = {
  monday: { active: true, start: '08:00', end: '17:00' },
  tuesday: { active: true, start: '08:00', end: '17:00' },
  wednesday: { active: true, start: '08:00', end: '17:00' },
  thursday: { active: true, start: '08:00', end: '17:00' },
  friday: { active: true, start: '08:00', end: '17:00' },
  saturday: { active: true, start: '08:00', end: '12:00' },
  sunday: { active: true, start: '08:00', end: '12:00' },
};

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const WEEK_DAYS = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá'];

const TIME_OFF_REASONS = [
  'Vacaciones',
  'Permiso médico',
  'Licencia de maternidad/paternidad',
  'Asuntos personales',
  'Duelo',
  'Capacitación',
  'Otro',
];

const DAY_NAMES_FULL = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

// ---- Mini Date Picker Component ----
interface MiniDatePickerProps {
  value: string; // YYYY-MM-DD
  onChange: (value: string) => void;
  placeholder?: string;
  countryCode?: string;
}

const MiniDatePicker: React.FC<MiniDatePickerProps> = ({ value, onChange, placeholder = 'Seleccionar', countryCode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => {
    if (value) {
      const [y, m] = value.split('-').map(Number);
      return new Date(y, m - 1, 1);
    }
    return new Date();
  });
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (value) {
      const [y, m] = value.split('-').map(Number);
      setViewMonth(new Date(y, m - 1, 1));
    }
  }, [value]);

  useEffect(() => {
    if (isOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const estimatedHeight = 300;
      const spaceBelow = window.innerHeight - rect.bottom;
      let left = rect.left;

      // If not enough space below, open above
      let top: number;
      if (spaceBelow >= estimatedHeight + 8) {
        top = rect.bottom + 4;
      } else {
        top = rect.top - estimatedHeight - 4;
      }

      // Clamp to viewport
      if (top < 8) top = 8;
      if (left + 260 > window.innerWidth - 8) {
        left = window.innerWidth - 260 - 8;
      }

      setPopoverPos({ top, left });
    }
  }, [isOpen]);

  const days = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = firstDay.getDay();
    const result: { date: Date; inMonth: boolean }[] = [];
    for (let i = startOffset - 1; i >= 0; i--) {
      result.push({ date: new Date(year, month, -i), inMonth: false });
    }
    for (let d = 1; d <= lastDay.getDate(); d++) {
      result.push({ date: new Date(year, month, d), inMonth: true });
    }
    const remaining = 7 - (result.length % 7);
    if (remaining < 7) {
      for (let d = 1; d <= remaining; d++) {
        result.push({ date: new Date(year, month + 1, d), inMonth: false });
      }
    }
    return result;
  }, [viewMonth]);

  const miniHolidayMap = useHolidayMap(viewMonth.getFullYear(), countryCode);

  const formatDisplay = (val: string) => {
    if (!val) return placeholder;
    const [y, m, d] = val.split('-').map(Number);
    return `${d} ${MONTH_NAMES[m - 1].slice(0, 3)} ${y}`;
  };

  const toDateStr = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
          value
            ? 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white hover:border-primary-400'
            : 'bg-gray-50 dark:bg-gray-800 border-dashed border-gray-300 dark:border-gray-600 text-gray-400 hover:border-primary-400 hover:text-primary-600'
        }`}
      >
        <CalendarDaysIcon className="h-3.5 w-3.5 flex-shrink-0" />
        {formatDisplay(value)}
      </button>

      {isOpen && createPortal(
        <div
          ref={popoverRef}
          className="fixed z-[99995] bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-3 w-[260px]"
          style={{ top: popoverPos.top, left: popoverPos.left }}
        >
          {/* Month nav */}
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
              <ChevronLeftIcon className="h-3.5 w-3.5" />
            </button>
            <span className="text-xs font-semibold text-gray-900 dark:text-white">
              {MONTH_NAMES[viewMonth.getMonth()]} {viewMonth.getFullYear()}
            </span>
            <button type="button" onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
              <ChevronRightIcon className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Week headers */}
          <div className="grid grid-cols-7 mb-1">
            {WEEK_DAYS.map((d) => (
              <div key={d} className="text-center text-[10px] font-medium text-gray-400 py-0.5">{d}</div>
            ))}
          </div>

          {/* Days */}
          <div className="grid grid-cols-7 gap-0.5">
            {days.map((item, idx) => {
              const dateStr = toDateStr(item.date);
              const isSelected = dateStr === value;
              const isToday = item.date.toDateString() === new Date().toDateString();
              const todayMidnight = new Date();
              todayMidnight.setHours(0, 0, 0, 0);
              const itemMidnight = new Date(item.date);
              itemMidnight.setHours(0, 0, 0, 0);
              const isPast = itemMidnight < todayMidnight;
              const isDisabled = !item.inMonth || isPast;
              const isHoliday = item.inMonth && miniHolidayMap.has(dateStr);

              return (
                <button
                  key={idx}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => {
                    if (!isDisabled) {
                      onChange(dateStr);
                      setIsOpen(false);
                    }
                  }}
                  className={`text-center text-xs py-1.5 rounded-md transition-colors relative ${
                    !item.inMonth
                      ? 'text-gray-300 dark:text-gray-600'
                      : isPast
                        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                        : isSelected
                          ? 'bg-primary-600 text-white font-semibold'
                          : isHoliday
                            ? 'text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/20 font-semibold hover:bg-blue-100 dark:hover:bg-blue-950/40'
                            : isToday
                              ? 'font-bold text-primary-600 dark:text-primary-400 ring-1 ring-primary-300 hover:bg-primary-50 dark:hover:bg-primary-950/20'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {item.date.getDate()}
                  {isHoliday && !isSelected && (
                    <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-blue-500" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Today button */}
          <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                onChange(toDateStr(today));
                setViewMonth(new Date(today.getFullYear(), today.getMonth(), 1));
                setIsOpen(false);
              }}
              className="w-full text-xs text-center py-1 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-950/20 rounded-md transition-colors font-medium"
            >
              Hoy
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

interface AdvisorScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  advisor: Advisor | null;
  onSaved?: () => void;
  countryCode?: string;
  teamAdvisors?: Advisor[];
  /**
   * Self-service save adapter. When provided, the modal persists the schedule
   * through this callback instead of `advisorService.updateSchedule` (which is
   * role-gated to advisors via /auth/advisors/). Used by the "Mi Horario" entry
   * in the avatar popover, where any tenant/platform user edits their own
   * schedule via the self endpoint /auth/users/profile/.
   */
  onSaveSchedule?: (schedule: AvailabilitySchedule) => Promise<void>;
}

export const AdvisorScheduleModal: React.FC<AdvisorScheduleModalProps> = ({
  isOpen,
  onClose,
  advisor,
  onSaved,
  countryCode,
  teamAdvisors,
  onSaveSchedule,
}) => {
  const [workSchedule, setWorkSchedule] = useState<Record<DayKey, DaySchedule>>(DEFAULT_SCHEDULE);
  const [timeOff, setTimeOff] = useState<TimeOffEntry[]>([]);
  const [restDays, setRestDays] = useState<string[]>([]); // YYYY-MM-DD dates
  const [restDayMode, setRestDayMode] = useState(false); // toggle for marking rest days on calendar
  const [isSaving, setIsSaving] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [timeOffExpanded, setTimeOffExpanded] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Almuerzo / descanso global (F1.1) — aplica a todos los días activos
  const [lunchEnabled, setLunchEnabled] = useState(false);
  const [lunchStart, setLunchStart] = useState('12:00');
  const [lunchEnd, setLunchEnd] = useState('13:00');

  useEffect(() => {
    if (advisor?.availability_schedule) {
      const schedule = advisor.availability_schedule;
      if (schedule.work_schedule && Object.keys(schedule.work_schedule).length > 0) {
        const loaded: Record<DayKey, DaySchedule> = { ...DEFAULT_SCHEDULE };
        for (const day of DAYS) {
          const dayData = schedule.work_schedule[day.key];
          if (dayData) {
            loaded[day.key] = {
              active: dayData.active ?? false,
              start: dayData.start ?? '08:00',
              end: dayData.end ?? '17:00',
            };
          }
        }
        setWorkSchedule(loaded);
      } else {
        setWorkSchedule(DEFAULT_SCHEDULE);
      }
      setTimeOff(
        (schedule.time_off || []).map((t) => ({
          start_date: t.start_date || '',
          end_date: t.end_date || '',
          reason: t.reason || '',
          saved: true,
        }))
      );
      setRestDays(schedule.rest_days || []);
      const firstBreak = (schedule.breaks || [])[0];
      if (firstBreak && firstBreak.start && firstBreak.end) {
        setLunchEnabled(true);
        setLunchStart(firstBreak.start);
        setLunchEnd(firstBreak.end);
      } else {
        setLunchEnabled(false);
      }
    } else {
      setWorkSchedule(DEFAULT_SCHEDULE);
      setTimeOff([]);
      setRestDays([]);
      setLunchEnabled(false);
    }
  }, [advisor]);

  const toggleDay = (day: DayKey) => {
    setWorkSchedule((prev) => ({
      ...prev,
      [day]: { ...prev[day], active: !prev[day].active },
    }));
  };

  const updateTime = (day: DayKey, field: 'start' | 'end', value: string) => {
    setWorkSchedule((prev) => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }));
  };

  const toggleRestDay = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    setRestDays((prev) =>
      prev.includes(dateStr)
        ? prev.filter((rd) => rd !== dateStr)
        : [...prev, dateStr]
    );
  };

  const addTimeOff = () => {
    setTimeOff((prev) => [...prev, { start_date: '', end_date: '', reason: '', saved: false }]);
  };

  const removeTimeOff = (index: number) => {
    setTimeOff((prev) => prev.filter((_, i) => i !== index));
  };

  const updateTimeOff = (index: number, field: keyof TimeOffEntry, value: string) => {
    setTimeOff((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry))
    );
  };

  const confirmTimeOffEntry = (index: number) => {
    const entry = timeOff[index];
    if (!entry.start_date || !entry.end_date) return;
    const cleanReason = entry.reason === '__otro__' ? '' : entry.reason;
    setTimeOff((prev) => prev.map((t, i) => (i === index ? { ...t, reason: cleanReason, saved: true } : t)));
  };

  const handleSave = async () => {
    if (!advisor) return;
    setIsSaving(true);

    // Coverage validation: ensure at least 1 advisor is active per day
    if (teamAdvisors && teamAdvisors.length > 1) {
      const uncoveredDays: string[] = [];
      for (const day of DAYS) {
        if (workSchedule[day.key].active) continue;
        const otherCovers = teamAdvisors.some((a) => {
          if (a.id === advisor.id) return false;
          const sched = a.availability_schedule?.work_schedule;
          return sched?.[day.key]?.active === true;
        });
        if (!otherCovers) uncoveredDays.push(day.label);
      }
      if (uncoveredDays.length > 0) {
        alert(
          `No se puede guardar: los siguientes días quedarían sin cobertura de asesores: ${uncoveredDays.join(', ')}. Debe haber al menos 1 asesor activo cada día.`
        );
        setIsSaving(false);
        return;
      }
    }

    try {
      const scheduleData: AvailabilitySchedule = {
        work_schedule: Object.fromEntries(
          DAYS.map((d) => [
            d.key,
            {
              active: workSchedule[d.key].active,
              start: workSchedule[d.key].start,
              end: workSchedule[d.key].end,
            },
          ])
        ) as AvailabilitySchedule['work_schedule'],
        time_off: timeOff
          .filter((t) => t.start_date && t.end_date)
          .map((t) => ({ ...t, reason: t.reason === '__otro__' ? '' : t.reason })),
        rest_days: restDays,
        breaks: lunchEnabled && lunchStart && lunchEnd ? [{ start: lunchStart, end: lunchEnd }] : [],
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      if (onSaveSchedule) {
        await onSaveSchedule(scheduleData);
      } else {
        await advisorService.updateSchedule(advisor.id, scheduleData);
      }
      // Mark all entries as saved after successful API call
      setTimeOff((prev) => prev.map((t) => (t.start_date && t.end_date ? { ...t, saved: true } : t)));
      onSaved?.();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (_error) {
      // Save failed
    } finally {
      setIsSaving(false);
    }
  };

  // Calendar
  const calendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = firstDay.getDay();
    const days: { date: Date; inMonth: boolean }[] = [];
    for (let i = startOffset - 1; i >= 0; i--) {
      days.push({ date: new Date(year, month, -i), inMonth: false });
    }
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push({ date: new Date(year, month, d), inMonth: true });
    }
    const remaining = 7 - (days.length % 7);
    if (remaining < 7) {
      for (let d = 1; d <= remaining; d++) {
        days.push({ date: new Date(year, month + 1, d), inMonth: false });
      }
    }
    return days;
  }, [calendarMonth]);

  // Holiday map for the visible month (and adjacent months for edge cases)
  const calendarYears = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const years = [year];
    if (month === 0) years.push(year - 1);
    if (month === 11) years.push(year + 1);
    return years;
  }, [calendarMonth]);

  const holidayMap = useHolidayMapMultiYear(calendarYears, countryCode);

  const getDayStatus = (date: Date): 'work' | 'off' | 'timeoff' | 'rest' | 'holiday' => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    // Check time-off first (highest priority)
    for (const t of timeOff) {
      if (t.start_date && t.end_date && dateStr >= t.start_date && dateStr <= t.end_date) {
        return 'timeoff';
      }
    }
    // Check holidays
    if (holidayMap.has(dateStr)) {
      return 'holiday';
    }
    // Check specific rest days
    if (restDays.includes(dateStr)) {
      return 'rest';
    }
    const dayKeys: DayKey[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayKey = dayKeys[date.getDay()];
    return workSchedule[dayKey]?.active ? 'work' : 'off';
  };

  const getTimeOffForDate = (date: Date): TimeOffEntry[] => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    return timeOff.filter((t) => t.start_date && t.end_date && dateStr >= t.start_date && dateStr <= t.end_date);
  };

  // Selected date info
  const selectedDayName = DAY_NAMES_FULL[selectedDate.getDay()];
  const selectedDateStr = `${selectedDate.getDate()} De ${MONTH_NAMES[selectedDate.getMonth()]}`;
  const selectedStatus = getDayStatus(selectedDate);
  const dayKeys: DayKey[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const selectedDayKey = dayKeys[selectedDate.getDay()];
  const selectedDaySchedule = workSchedule[selectedDayKey];

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[99990] overflow-y-auto">
      <div className="fixed inset-0 bg-black/25 dark:bg-gray-950/50" onClick={onClose} />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-[920px] bg-white dark:bg-gray-900 rounded-xl shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Gestión de Horarios
              </h2>
              {advisor && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {advisor.full_name || `${advisor.first_name} ${advisor.last_name}`} &bull; {advisor.email}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-500 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Horizontal Layout */}
          <div className="flex flex-col lg:flex-row">
            {/* LEFT: Calendar + Day Detail */}
            <div className="w-full lg:w-[420px] flex flex-col border-r border-gray-200 dark:border-gray-700">
              {/* Calendar */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <button type="button" onClick={() => setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                    {MONTH_NAMES[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
                  </h4>
                  <button type="button" onClick={() => setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>

                {/* Week headers */}
                <div className="grid grid-cols-7 mb-2">
                  {WEEK_DAYS.map((d) => (
                    <div key={d} className="text-center text-xs font-semibold text-gray-500 dark:text-gray-400 py-1.5">{d}</div>
                  ))}
                </div>

                {/* Days grid */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((item, idx) => {
                    const status = item.inMonth ? getDayStatus(item.date) : null;
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const itemDate = new Date(item.date);
                    itemDate.setHours(0, 0, 0, 0);
                    const isPast = itemDate < today;
                    const isToday = item.date.toDateString() === new Date().toDateString();
                    const isSelected = item.date.toDateString() === selectedDate.toDateString();
                    const isDisabled = !item.inMonth || isPast;
                    const dayTimeOffs = item.inMonth && status === 'timeoff' ? getTimeOffForDate(item.date) : [];
                    const holidayName = item.inMonth ? (() => {
                      const y = item.date.getFullYear();
                      const m = String(item.date.getMonth() + 1).padStart(2, '0');
                      const d = String(item.date.getDate()).padStart(2, '0');
                      return holidayMap.get(`${y}-${m}-${d}`) || '';
                    })() : '';
                    const tooltipText = dayTimeOffs.length > 0
                      ? dayTimeOffs.map((t) => t.reason || 'Permiso').join(', ')
                      : status === 'holiday' ? holidayName
                      : status === 'rest' ? 'Día de descanso' : '';

                    let dayClass = 'text-gray-300 dark:text-gray-600';
                    if (item.inMonth) {
                      if (isPast) {
                        dayClass = 'bg-gray-50 dark:bg-gray-800/30 text-gray-300 dark:text-gray-600 cursor-not-allowed';
                      } else if (status === 'timeoff') {
                        dayClass = 'bg-red-100 dark:bg-red-950/30 text-red-600 dark:text-red-400 font-semibold hover:bg-red-200 dark:hover:bg-red-950/50';
                      } else if (status === 'holiday') {
                        dayClass = 'bg-blue-100 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 font-semibold hover:bg-blue-200 dark:hover:bg-blue-950/50';
                      } else if (status === 'rest') {
                        dayClass = 'bg-amber-100 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 font-semibold hover:bg-amber-200 dark:hover:bg-amber-950/50';
                      } else if (status === 'work') {
                        dayClass = 'bg-green-50 dark:bg-green-950/20 text-green-800 dark:text-green-300 font-medium hover:bg-green-100 dark:hover:bg-green-950/40';
                      } else {
                        dayClass = 'bg-gray-100 dark:bg-gray-800/50 text-gray-400 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-800';
                      }
                    }

                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          if (isDisabled) return;
                          setSelectedDate(item.date);
                          // Only toggle rest day when restDayMode is on
                          if (restDayMode && (status === 'work' || status === 'rest')) {
                            toggleRestDay(item.date);
                          }
                        }}
                        disabled={isDisabled}
                        className={`text-center text-sm py-2.5 rounded-lg transition-colors relative group ${dayClass} ${
                          isSelected && item.inMonth && !isPast ? '!bg-primary-600 !text-white hover:!bg-primary-700' : ''
                        } ${isToday && !isSelected ? 'font-bold ring-1 ring-primary-400' : ''}`}
                      >
                        {item.date.getDate()}
                        {tooltipText && !isSelected && (
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-[10px] font-medium text-white bg-gray-900 dark:bg-gray-700 rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 max-w-[150px] truncate">
                            {tooltipText}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Rest day mode toggle */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setRestDayMode((prev) => !prev)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${
                        restDayMode ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform ${
                        restDayMode ? 'translate-x-5' : 'translate-x-0.5'
                      }`} />
                    </button>
                    <span className={`text-xs font-medium ${restDayMode ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      Marcar descansos
                    </span>
                  </div>
                  {restDayMode && (
                    <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400">
                      Click en un día para marcar/quitar descanso
                    </span>
                  )}
                </div>

                {/* Legend */}
                <div className="flex items-center flex-wrap gap-3 mt-2">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800" />
                    <span className="text-[10px] text-gray-500">Laboral</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-blue-100 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800" />
                    <span className="text-[10px] text-gray-500">Festivo</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-amber-100 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800" />
                    <span className="text-[10px] text-gray-500">Descanso</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-gray-100 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700" />
                    <span className="text-[10px] text-gray-500">No laboral</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-red-100 dark:bg-red-950/30 border border-red-200 dark:border-red-800" />
                    <span className="text-[10px] text-gray-500">Permiso</span>
                  </div>
                </div>
              </div>

              {/* Selected Day Detail */}
              <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-3">
                <p className="text-xs font-semibold text-gray-900 dark:text-white mb-1.5">
                  {selectedDayName}, {selectedDateStr}
                </p>
                {selectedStatus === 'timeoff' ? (
                  <div className="space-y-1.5">
                    {getTimeOffForDate(selectedDate).map((t, i) => {
                      const formatDate = (ds: string) => {
                        const [y, m, day] = ds.split('-').map(Number);
                        return `${day} ${MONTH_NAMES[m - 1].slice(0, 3)} ${y}`;
                      };
                      return (
                        <div key={i} className="p-2 rounded-lg border bg-red-50 dark:bg-red-950/20 border-red-100 dark:border-red-900/30">
                          <p className="text-xs font-medium text-red-700 dark:text-red-400">
                            Permiso / Vacaciones
                          </p>
                          <p className="text-[11px] mt-0.5 text-red-500 dark:text-red-300">
                            {formatDate(t.start_date)} — {formatDate(t.end_date)}
                          </p>
                          <p className="text-[11px] font-medium text-red-600 dark:text-red-300">
                            {t.reason || 'Sin motivo especificado'}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                ) : selectedStatus === 'holiday' ? (
                  <div className="p-2 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                    <p className="text-xs text-blue-700 dark:text-blue-400 font-medium">Día festivo</p>
                    <p className="text-[11px] text-blue-600 dark:text-blue-300 mt-0.5">
                      {(() => {
                        const y = selectedDate.getFullYear();
                        const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
                        const d = String(selectedDate.getDate()).padStart(2, '0');
                        return holidayMap.get(`${y}-${m}-${d}`) || '';
                      })()}
                    </p>
                  </div>
                ) : selectedStatus === 'rest' ? (
                  <div className="p-2 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                    <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">Día de descanso</p>
                    <p className="text-[11px] text-amber-600 dark:text-amber-300 mt-0.5">
                      Click en el día para quitar el descanso
                    </p>
                  </div>
                ) : selectedStatus === 'work' ? (
                  <div className="p-2 bg-green-50 dark:bg-green-950/20 rounded-lg">
                    <p className="text-xs text-green-700 dark:text-green-400 font-medium">Día laboral</p>
                    <p className="text-[11px] text-green-600 dark:text-green-300 mt-0.5">
                      {selectedDaySchedule?.start} - {selectedDaySchedule?.end}
                    </p>
                  </div>
                ) : (
                  <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-xs text-gray-500 font-medium">Día no laboral</p>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT: Schedule + Time Off */}
            <div className="flex-1 max-w-[480px] p-4 space-y-4">
              {/* Weekly Schedule */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <ClockIcon className="h-4 w-4 text-primary-500" />
                  Horario Semanal
                </h3>
                <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                  {/* Table header */}
                  <div className="grid grid-cols-[auto_auto_1fr_auto_1fr] bg-gray-50 dark:bg-gray-800/80 px-3 py-1.5 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 text-center px-2">Estado</span>
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400">Día</span>
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 text-center">Entrada</span>
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 text-center px-1"></span>
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 text-center">Salida</span>
                  </div>
                  {DAYS.map((day, i) => {
                    const schedule = workSchedule[day.key];
                    return (
                      <div
                        key={day.key}
                        className={`grid grid-cols-[auto_auto_1fr_auto_1fr] items-center px-3 py-2 ${
                          i < DAYS.length - 1 ? 'border-b border-gray-100 dark:border-gray-800' : ''
                        } ${schedule.active ? '' : 'bg-gray-50/50 dark:bg-gray-800/30'}`}
                      >
                        <div className="px-2 flex justify-center">
                          <button
                            type="button"
                            onClick={() => toggleDay(day.key)}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${
                              schedule.active ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                            }`}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform ${
                              schedule.active ? 'translate-x-5' : 'translate-x-0.5'
                            }`} />
                          </button>
                        </div>
                        <div className="min-w-[90px]">
                          <span className={`text-sm font-medium ${schedule.active ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>
                            {day.label}
                          </span>
                        </div>
                        <input
                          type="time"
                          value={schedule.start}
                          onChange={(e) => updateTime(day.key, 'start', e.target.value)}
                          disabled={!schedule.active}
                          className={`text-xs border rounded-lg px-2 py-1.5 w-full text-center ${
                            schedule.active
                              ? 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-1 focus:ring-primary-300 focus:border-primary-300 outline-none'
                              : 'border-gray-100 dark:border-gray-700 bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                          }`}
                        />
                        <span className="text-xs text-gray-300 dark:text-gray-600 text-center px-1">—</span>
                        <input
                          type="time"
                          value={schedule.end}
                          onChange={(e) => updateTime(day.key, 'end', e.target.value)}
                          disabled={!schedule.active}
                          className={`text-xs border rounded-lg px-2 py-1.5 w-full text-center ${
                            schedule.active
                              ? 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-1 focus:ring-primary-300 focus:border-primary-300 outline-none'
                              : 'border-gray-100 dark:border-gray-700 bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                          }`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Almuerzo / descanso global (F1.1) — no se agendan citas en esta franja */}
              <div className="flex items-center justify-between gap-3 px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setLunchEnabled((v) => !v)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${
                      lunchEnabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform ${
                      lunchEnabled ? 'translate-x-5' : 'translate-x-0.5'
                    }`} />
                  </button>
                  <span className={`text-sm font-medium ${lunchEnabled ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>
                    Almuerzo <span className="text-gray-400 font-normal">(no se agendan citas)</span>
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <input
                    type="time"
                    value={lunchStart}
                    onChange={(e) => setLunchStart(e.target.value)}
                    disabled={!lunchEnabled}
                    className={`text-xs border rounded-lg px-2 py-1.5 w-[88px] text-center ${
                      lunchEnabled
                        ? 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-1 focus:ring-primary-300 focus:border-primary-300 outline-none'
                        : 'border-gray-100 dark:border-gray-700 bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                    }`}
                  />
                  <span className="text-xs text-gray-300 dark:text-gray-600 px-1">—</span>
                  <input
                    type="time"
                    value={lunchEnd}
                    onChange={(e) => setLunchEnd(e.target.value)}
                    disabled={!lunchEnabled}
                    className={`text-xs border rounded-lg px-2 py-1.5 w-[88px] text-center ${
                      lunchEnabled
                        ? 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-1 focus:ring-primary-300 focus:border-primary-300 outline-none'
                        : 'border-gray-100 dark:border-gray-700 bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                    }`}
                  />
                </div>
              </div>

              {/* Time Off - Collapsible */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800/80">
                  <button
                    type="button"
                    onClick={() => setTimeOffExpanded(!timeOffExpanded)}
                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                  >
                    <CalendarIcon className="h-4 w-4 text-orange-500" />
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">Permisos y Ausencias</span>
                    {timeOff.length > 0 && (
                      <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 text-[10px] font-bold text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-950/30 rounded-full">
                        {timeOff.length}
                      </span>
                    )}
                    <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${timeOffExpanded ? 'rotate-180' : ''}`} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); addTimeOff(); setTimeOffExpanded(true); }}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-950/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-950/30 transition-colors"
                  >
                    <PlusIcon className="h-3.5 w-3.5" />
                    Agregar
                  </button>
                </div>
                {timeOffExpanded && (
                  <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                    {timeOff.length === 0 ? (
                      <div className="text-center py-4 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
                        <CalendarDaysIcon className="h-6 w-6 text-gray-300 dark:text-gray-600 mx-auto mb-1" />
                        <p className="text-xs text-gray-400 dark:text-gray-500">Sin permisos programados</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {timeOff.map((entry, index) => {
                          const formatDate = (d: string) => {
                            if (!d) return '';
                            const [y, m, day] = d.split('-').map(Number);
                            return `${day} ${MONTH_NAMES[m - 1].slice(0, 3)} ${y}`;
                          };

                          if (entry.saved) {
                            // Confirmed entry - static display
                            return (
                              <div key={index} className="flex items-center gap-3 p-3 bg-orange-50 dark:bg-orange-950/10 border border-orange-200 dark:border-orange-800/50 rounded-xl">
                                <CheckCircleIcon className="h-4 w-4 text-orange-500 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-gray-900 dark:text-white">
                                    {formatDate(entry.start_date)} — {formatDate(entry.end_date)}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {entry.reason || 'Sin motivo'}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <button type="button" onClick={() => setTimeOff((prev) => prev.map((e, i) => i === index ? { ...e, saved: false } : e))}
                                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-950/20 rounded-lg transition-colors">
                                    <PencilIcon className="h-3.5 w-3.5" />
                                  </button>
                                  <button type="button" onClick={() => removeTimeOff(index)}
                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-lg transition-colors">
                                    <TrashIcon className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </div>
                            );
                          }

                          // Unsaved entry - editable
                          return (
                            <div key={index} className="p-2.5 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/10 dark:to-amber-950/10 border border-orange-200 dark:border-orange-800/50 rounded-xl">
                              <div className="flex items-center gap-2 mb-1.5">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-[10px] uppercase tracking-wider font-semibold text-orange-500 dark:text-orange-400">Desde</span>
                                  <MiniDatePicker
                                    value={entry.start_date}
                                    onChange={(v) => updateTimeOff(index, 'start_date', v)}
                                    placeholder="Fecha inicio"
                                    countryCode={countryCode}
                                  />
                                  <span className="text-[10px] uppercase tracking-wider font-semibold text-orange-500 dark:text-orange-400 ml-1">Hasta</span>
                                  <MiniDatePicker
                                    value={entry.end_date}
                                    onChange={(v) => updateTimeOff(index, 'end_date', v)}
                                    placeholder="Fecha fin"
                                    countryCode={countryCode}
                                  />
                                </div>
                                <button type="button" onClick={() => removeTimeOff(index)}
                                  className="ml-auto p-1.5 text-red-400 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-950/20 rounded-lg transition-colors flex-shrink-0">
                                  <TrashIcon className="h-3.5 w-3.5" />
                                </button>
                              </div>
                              <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                  <select
                                    value={TIME_OFF_REASONS.includes(entry.reason) ? entry.reason : entry.reason ? 'Otro' : ''}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      if (val === 'Otro') {
                                        updateTimeOff(index, 'reason', '__otro__');
                                      } else {
                                        updateTimeOff(index, 'reason', val);
                                      }
                                    }}
                                    className="flex-1 text-xs border border-orange-200 dark:border-orange-800/50 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-1 focus:ring-orange-300 focus:border-orange-300 outline-none transition-colors"
                                  >
                                    <option value="" disabled>Motivo del permiso...</option>
                                    {TIME_OFF_REASONS.map((reason) => (
                                      <option key={reason} value={reason}>{reason}</option>
                                    ))}
                                  </select>
                                  <button
                                  type="button"
                                  onClick={() => confirmTimeOffEntry(index)}
                                  disabled={!entry.start_date || !entry.end_date}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                                >
                                  <CheckCircleIcon className="h-3.5 w-3.5" />
                                  Confirmar
                                </button>
                                </div>
                                {(entry.reason === '__otro__' || (!TIME_OFF_REASONS.includes(entry.reason) && entry.reason !== '' && entry.reason)) && (
                                  <input
                                    type="text"
                                    value={entry.reason === '__otro__' ? '' : entry.reason}
                                    onChange={(e) => updateTimeOff(index, 'reason', e.target.value || '__otro__')}
                                    placeholder="Especifique el motivo..."
                                    className="w-full text-xs border border-orange-200 dark:border-orange-800/50 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-orange-300 focus:border-orange-300 outline-none transition-colors"
                                  />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 px-6 py-3">
            <div>
              {saveSuccess && (
                <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-medium">Horario guardado correctamente</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button onClick={handleSave} disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50">
                {isSaving ? 'Guardando...' : 'Guardar Horario'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
